// Feature: features/usn-biller-icd10-compliance-tab.md
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  Flex,
  Input,
  Button,
  HStack,
  VStack,
  Badge,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Select,
  Alert,
  AlertIcon,
  Link,
  Divider,
} from '@chakra-ui/react';
import { ExternalLinkIcon, RepeatIcon } from '@chakra-ui/icons';
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { callApi } from '../callApi';

const GROUP_OPTIONS = [
  { label: 'Biller Team (Direct Employee -> Biller Map)', value: 'biller_team' },
  { label: 'Biller Employee (Individual Biller / Coder)', value: 'biller_employee' },
  { label: 'Region / Org (MC / MC-US IOM / ANS)', value: 'region_org' },
  { label: 'Payer Group (BCBS / UHC / Aetna / Medicare / etc.)', value: 'payer_group' },
  { label: 'Carrier / Insurance Company Name', value: 'carrier' },
];

const normalizePayerGroupClient = (carrier, insType) => {
  const combined = `${carrier || ''} ${insType || ''}`.trim().toLowerCase();
  if (!combined || combined === 'unassigned') return 'Unassigned / Blank Carrier';
  if (
    [
      'bcbs',
      'blue cross',
      'blue shield',
      'anthem',
      'highmark',
      'premera',
      'regence',
      'carefirst',
    ].some((k) => combined.includes(k))
  ) {
    return 'BCBS';
  }
  if (
    ['united', 'uhc', 'umr', 'aarp', 'surest', 'optum', 'wellmed', 'oxford'].some((k) =>
      combined.includes(k)
    )
  ) {
    return 'United Healthcare / UMR';
  }
  if (combined.includes('cigna') || combined.includes('evernorth')) return 'Cigna';
  if (combined.includes('aetna') || combined.includes('meritain')) return 'Aetna';
  if (combined.includes('humana')) return 'Humana';
  if (
    [
      'centene',
      'wellcare',
      'superior',
      'ambetter',
      'amerigroup',
      'wellpoint',
      'molina',
      'health spring',
    ].some((k) => combined.includes(k))
  ) {
    return 'Centene / Managed Medicaid-MA';
  }
  if (['triwest', 'va ', 'veteran', 'tricare', 'champva'].some((k) => combined.includes(k))) {
    return 'VA / TriWest / Military';
  }
  if (['attorney', 'lop', 'letter of protection'].some((k) => combined.includes(k))) {
    return 'Attorney / LOP';
  }
  if (
    ['workmans comp', 'workers comp', 'work comp', 'dol', 'department of labor'].some((k) =>
      combined.includes(k)
    )
  ) {
    return 'Workers Comp / DOL';
  }
  if (
    ['medicare', 'novitas', 'palmetto', 'noridian', 'wps', 'first coast', 'cgs'].some((k) =>
      combined.includes(k)
    )
  ) {
    return 'Medicare / Medicare Advantage';
  }
  return 'Other Commercial / Regional';
};

const getClaimPayerGroup = (c) =>
  (c?.payer_group || '').trim() || normalizePayerGroupClient(c?.carrier, c?.insurance_type);

const DIFF_FILTER_OPTIONS = [
  { label: 'Any ICD-10 Difference (Not Exact Sequence Match)', value: 'any_diff' },
  { label: 'Primary Mismatch Only (Sub #1 != Revedy #1)', value: 'primary_diff' },
  {
    label: 'Secondary Diff Only (Primary Matches, Secondary Differs)',
    value: 'secondary_diff_only',
  },
  { label: 'Added Non-Revedy ICD-10s (Zero Overlap / Partial / Superset)', value: 'added_non_revedy' },
  { label: 'All Pre-Filing Coded Claims (Incl. Exact Matches)', value: 'all_revedy' },
];

const PRIMARY_MATCH_FILTER_OPTIONS = [
  { label: 'All (Primary Match & Mismatch)', value: 'ALL' },
  { label: 'Primary Mismatch Only (Sub #1 != Revedy #1)', value: 'mismatch' },
  { label: 'Primary Match Only (Sub #1 == Revedy #1)', value: 'match' },
  { label: 'Used Revedy Secondary as Primary', value: 'secondary_as_primary' },
  { label: 'Non-Revedy Primary Dx', value: 'non_revedy_primary' },
];

const APPROVAL_SCORE_FILTER_OPTIONS = [
  { label: 'All Approval Scores', value: 'ALL' },
  { label: 'High Approval (>= 80)', value: 'gte_80' },
  { label: 'Medium Approval (60 - 79)', value: '60_79' },
  { label: 'Low Approval (< 60)', value: 'lt_60' },
  { label: 'Very Low Approval (< 30)', value: 'lt_30' },
];

const NEEDS_REVIEW_SCORE_FILTER_OPTIONS = [
  { label: 'All Needs Review Scores', value: 'ALL' },
  { label: 'High Needs Review (>= 80)', value: 'gte_80' },
  { label: 'Moderate+ Needs Review (>= 60)', value: 'gte_60' },
  { label: 'Elevated Needs Review (40 - 79)', value: '40_79' },
  { label: 'Low Needs Review (< 40)', value: 'lt_40' },
];

const DX_REVIEW_SCORE_FILTER_OPTIONS = [
  { label: 'All Dx Review Scores', value: 'ALL' },
  { label: 'High Dx Review (>= 80)', value: 'gte_80' },
  { label: 'Moderate+ Dx Review (>= 60)', value: 'gte_60' },
  { label: 'Elevated Dx Review (40 - 79)', value: '40_79' },
  { label: 'Low Dx Review (< 40)', value: 'lt_40' },
];

const POST_FILING_SUB_FILTER_OPTIONS = [
  { label: 'All Cases Filed Prior to Revedy Coding', value: 'ALL' },
  { label: 'Case Uploaded After Filing Date', value: 'Case Uploaded After Filing Date' },
  {
    label: 'Case Uploaded <= Filing Date, ICD Coded After Filing Date',
    value: 'Case Uploaded <= Filing Date, ICD Coded After Filing Date',
  },
];

const escapeCSVCell = (val) => {
  const s = val == null ? '' : String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const downloadCSVFile = (headers, rows, filenamePrefix) => {
  const csvContent = [
    headers.join(','),
    ...rows.map((r) => r.map(escapeCSVCell).join(',')),
  ].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute(
    'download',
    `${filenamePrefix}_${new Date().toISOString().split('T')[0]}.csv`
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const USNBillerComplianceTab = ({ accessToken }) => {
  const fileInputRef = useRef(null);

  const [claims, setClaims] = useState([]);
  const [filedPriorClaims, setFiledPriorClaims] = useState([]);
  const [excludedClaims, setExcludedClaims] = useState([]);
  const [issueSummary, setIssueSummary] = useState([]);
  const [fileMeta, setFileMeta] = useState(null);

  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [error, setError] = useState(null);
  const [statusMsg, setStatusMsg] = useState(null);

  // Top-Level Exclusion / Date Overlays (applied first across all views)
  const [excludedBillerTeams, setExcludedBillerTeams] = useState([]);
  const [excludedPayerGroups, setExcludedPayerGroups] = useState([]);
  const [filingStartDate, setFilingStartDate] = useState('');
  const [filingEndDate, setFilingEndDate] = useState('');

  // Active Report View: 'pre_filing_compliance' | 'filed_prior_to_coding' | 'excluded_issues'
  const [activeView, setActiveView] = useState('pre_filing_compliance');

  // Shared Group & Time Filters
  const [groupCol, setGroupCol] = useState('biller_team');
  const [selectedGroupVal, setSelectedGroupVal] = useState('ALL');
  const [timeAxis, setTimeAxis] = useState('dos');
  const [timeGrain, setTimeGrain] = useState('week');

  // Pre-Filing Compliance Table Filters
  const [diffFilterMode, setDiffFilterMode] = useState('any_diff');
  const [primaryMatchFilter, setPrimaryMatchFilter] = useState('ALL');
  const [approvalScoreFilter, setApprovalScoreFilter] = useState('ALL');
  const [needsReviewScoreFilter, setNeedsReviewScoreFilter] = useState('ALL');
  const [dxReviewScoreFilter, setDxReviewScoreFilter] = useState('ALL');
  const [recentLimit, setRecentLimit] = useState(200);
  const [searchQuery, setSearchQuery] = useState('');

  // Filed Prior to Revedy Coding Filters
  const [postFilingSubFilter, setPostFilingSubFilter] = useState('ALL');
  const [postFilingSearch, setPostFilingSearch] = useState('');
  const [postFilingLimit, setPostFilingLimit] = useState(200);

  // Excluded Claims & Issues Filters
  const [selectedIssueReason, setSelectedIssueReason] = useState('ALL');
  const [excludedSearch, setExcludedSearch] = useState('');
  const [excludedLimit, setExcludedLimit] = useState(200);

  const enrichWithPayerGroup = (arr) =>
    (arr || []).map((c) => ({
      ...c,
      payer_group: getClaimPayerGroup(c),
    }));

  const applyApiPayload = (result) => {
    setClaims(enrichWithPayerGroup(result.claims));
    setFiledPriorClaims(enrichWithPayerGroup(result.filed_prior_to_coding_claims));
    setExcludedClaims(enrichWithPayerGroup(result.excluded_claims));
    setIssueSummary(result.issue_summary || []);
    setFileMeta({
      upload_id: result.upload_id,
      user_id: result.user_id,
      org_id: result.org_id,
      file_type: result.file_type,
      filename: result.filename,
      row_count: result.row_count,
      record_count: result.record_count,
      created_at: result.created_at,
      rebuilt_at: result.rebuilt_at,
      metadata: result.metadata || {},
    });
  };

  const fetchComplianceData = async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const res = await callApi('/usn/biller-compliance', accessToken, { method: 'GET' });
      const result = res ? await res.json().catch(() => null) : null;
      if (res && res.ok && result?.status === 'success') {
        applyApiPayload(result);
      } else {
        setError(
          result?.message || `Server returned ${res ? res.status : 'no response'}`
        );
      }
    } catch (err) {
      console.error('Error loading USN biller compliance:', err);
      setError('Failed to load USN biller compliance data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComplianceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !accessToken) return;

    setUploading(true);
    setError(null);
    setStatusMsg(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', 'usn_usmon_billing_report');

      const res = await callApi('/usn/biller-compliance/upload', accessToken, {
        method: 'POST',
        body: formData,
      });
      const result = res ? await res.json().catch(() => null) : null;
      if (res && res.ok && result?.status === 'success') {
        applyApiPayload(result);
        setStatusMsg(
          `Uploaded "${result.filename}" (${(result.row_count || 0).toLocaleString()} raw rows -> ${(result.record_count || 0).toLocaleString()} total claims: ${(result.claims || []).length.toLocaleString()} pre-filing compliance, ${(result.filed_prior_to_coding_claims || []).length.toLocaleString()} filed prior to Revedy coding, ${(result.excluded_claims || []).length.toLocaleString()} excluded w/ data issues).`
        );
      } else {
        setError(
          result?.message || `Upload failed (HTTP ${res ? res.status : 'error'})`
        );
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('An error occurred while uploading the file.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRebuild = async () => {
    if (!accessToken) return;
    setRebuilding(true);
    setError(null);
    setStatusMsg(null);
    try {
      const res = await callApi('/usn/biller-compliance/rebuild', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_type: 'usn_usmon_billing_report' }),
      });
      const result = res ? await res.json().catch(() => null) : null;
      if (res && res.ok && result?.status === 'success') {
        applyApiPayload(result);
        setStatusMsg(
          `Rebuilt ${(result.record_count || 0).toLocaleString()} total claims from "${result.filename}": ${(result.claims || []).length.toLocaleString()} pre-filing compliance claims, ${(result.filed_prior_to_coding_claims || []).length.toLocaleString()} filed prior to Revedy coding, and ${(result.excluded_claims || []).length.toLocaleString()} excluded w/ data issues.`
        );
      } else {
        setError(
          result?.message || `Rebuild failed (HTTP ${res ? res.status : 'error'})`
        );
      }
    } catch (err) {
      console.error('Rebuild error:', err);
      setError('An error occurred while rebuilding from the stored file.');
    } finally {
      setRebuilding(false);
    }
  };

  const bucketCol = useMemo(() => {
    if (timeAxis === 'filing_date') {
      return timeGrain === 'week' ? 'filing_week' : 'filing_month';
    }
    return timeGrain === 'week' ? 'dos_week' : 'dos_month';
  }, [timeAxis, timeGrain]);

  // ============================================================================
  // TOP-LEVEL OVERLAYS (Applied First Across All Views: Biller Team, Payer Group,
  // and CMS-1500 Submitted Date Range)
  // ============================================================================
  const normalizeBillerTeamKey = (team) => {
    const t = (team || '').trim();
    return t || '(Unmapped / Blank)';
  };

  const allBillerTeamOptions = useMemo(() => {
    const map = {};
    const ensureEntry = (teamKey) => {
      if (!map[teamKey]) {
        map[teamKey] = {
          teamKey,
          preFilingCount: 0,
          postFilingCount: 0,
          excludedCount: 0,
          totalCount: 0,
        };
      }
      return map[teamKey];
    };

    claims.forEach((c) => {
      const entry = ensureEntry(normalizeBillerTeamKey(c.biller_team));
      entry.preFilingCount += 1;
      entry.totalCount += 1;
    });
    filedPriorClaims.forEach((c) => {
      const entry = ensureEntry(normalizeBillerTeamKey(c.biller_team));
      entry.postFilingCount += 1;
      entry.totalCount += 1;
    });
    excludedClaims.forEach((c) => {
      const entry = ensureEntry(normalizeBillerTeamKey(c.biller_team));
      entry.excludedCount += 1;
      entry.totalCount += 1;
    });

    return Object.values(map).sort((a, b) => {
      if (b.preFilingCount !== a.preFilingCount) {
        return b.preFilingCount - a.preFilingCount;
      }
      return b.totalCount - a.totalCount;
    });
  }, [claims, filedPriorClaims, excludedClaims]);

  const allPayerGroupOptions = useMemo(() => {
    const map = {};
    const ensureEntry = (pgKey) => {
      if (!map[pgKey]) {
        map[pgKey] = {
          payerGroupKey: pgKey,
          preFilingCount: 0,
          postFilingCount: 0,
          excludedCount: 0,
          totalCount: 0,
        };
      }
      return map[pgKey];
    };

    claims.forEach((c) => {
      const entry = ensureEntry(getClaimPayerGroup(c));
      entry.preFilingCount += 1;
      entry.totalCount += 1;
    });
    filedPriorClaims.forEach((c) => {
      const entry = ensureEntry(getClaimPayerGroup(c));
      entry.postFilingCount += 1;
      entry.totalCount += 1;
    });
    excludedClaims.forEach((c) => {
      const entry = ensureEntry(getClaimPayerGroup(c));
      entry.excludedCount += 1;
      entry.totalCount += 1;
    });

    return Object.values(map).sort((a, b) => {
      if (b.preFilingCount !== a.preFilingCount) {
        return b.preFilingCount - a.preFilingCount;
      }
      return b.totalCount - a.totalCount;
    });
  }, [claims, filedPriorClaims, excludedClaims]);

  const filingDateBounds = useMemo(() => {
    let minDate = '';
    let maxDate = '';
    const checkDate = (d) => {
      if (!d || !/^\d{4}-\d{2}-\d{2}$/.test(d)) return;
      if (!minDate || d < minDate) minDate = d;
      if (!maxDate || d > maxDate) maxDate = d;
    };
    claims.forEach((c) => checkDate(c.filing_date));
    filedPriorClaims.forEach((c) => checkDate(c.filing_date));
    excludedClaims.forEach((c) => checkDate(c.filing_date));
    return { minDate, maxDate };
  }, [claims, filedPriorClaims, excludedClaims]);

  const toggleExcludeBillerTeam = (teamKey) => {
    setExcludedBillerTeams((prev) =>
      prev.includes(teamKey) ? prev.filter((t) => t !== teamKey) : [...prev, teamKey]
    );
  };

  const toggleExcludePayerGroup = (pgKey) => {
    setExcludedPayerGroups((prev) =>
      prev.includes(pgKey) ? prev.filter((p) => p !== pgKey) : [...prev, pgKey]
    );
  };

  const hasAnyTopLevelOverlay =
    excludedBillerTeams.length > 0 ||
    excludedPayerGroups.length > 0 ||
    Boolean(filingStartDate) ||
    Boolean(filingEndDate);

  const matchesTopLevelOverlays = (c) => {
    if (
      excludedBillerTeams.length > 0 &&
      excludedBillerTeams.includes(normalizeBillerTeamKey(c.biller_team))
    ) {
      return false;
    }
    if (
      excludedPayerGroups.length > 0 &&
      excludedPayerGroups.includes(getClaimPayerGroup(c))
    ) {
      return false;
    }
    if (filingStartDate) {
      const fDate = (c.filing_date || '').slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(fDate) || fDate < filingStartDate) {
        return false;
      }
    }
    if (filingEndDate) {
      const fDate = (c.filing_date || '').slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(fDate) || fDate > filingEndDate) {
        return false;
      }
    }
    return true;
  };

  const baseClaims = useMemo(() => {
    if (!hasAnyTopLevelOverlay) return claims;
    return claims.filter(matchesTopLevelOverlays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claims, excludedBillerTeams, excludedPayerGroups, filingStartDate, filingEndDate]);

  const baseFiledPriorClaims = useMemo(() => {
    if (!hasAnyTopLevelOverlay) return filedPriorClaims;
    return filedPriorClaims.filter(matchesTopLevelOverlays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filedPriorClaims,
    excludedBillerTeams,
    excludedPayerGroups,
    filingStartDate,
    filingEndDate,
  ]);

  const baseExcludedClaims = useMemo(() => {
    if (!hasAnyTopLevelOverlay) return excludedClaims;
    return excludedClaims.filter(matchesTopLevelOverlays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludedClaims, excludedBillerTeams, excludedPayerGroups, filingStartDate, filingEndDate]);

  const baseTotalClaimsCount =
    baseClaims.length + baseFiledPriorClaims.length + baseExcludedClaims.length;
  const rawTotalClaimsCount =
    claims.length + filedPriorClaims.length + excludedClaims.length;
  const hiddenByOverlayCount = Math.max(0, rawTotalClaimsCount - baseTotalClaimsCount);

  const baseIssueSummary = useMemo(() => {
    if (!hasAnyTopLevelOverlay) return issueSummary;
    const counts = {};
    baseExcludedClaims.forEach((c) => {
      const reasons =
        Array.isArray(c.issue_reasons) && c.issue_reasons.length > 0
          ? c.issue_reasons
          : c.primary_issue
          ? [c.primary_issue]
          : [];
      reasons.forEach((r) => {
        counts[r] = (counts[r] || 0) + 1;
      });
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([reason, count]) => ({
        issue_reason: reason,
        claim_count: count,
        pct_of_total_claims:
          baseTotalClaimsCount > 0
            ? Number(((count / baseTotalClaimsCount) * 100).toFixed(1))
            : 0.0,
      }));
  }, [hasAnyTopLevelOverlay, issueSummary, baseExcludedClaims, baseTotalClaimsCount]);

  // ============================================================================
  // VIEW 1: Pre-Filing Biller Compliance (`baseClaims`)
  // ============================================================================
  const availableGroupValues = useMemo(() => {
    const counts = {};
    baseClaims.forEach((c) => {
      const g = c[groupCol];
      if (!g) return;
      counts[g] = (counts[g] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([val, cnt]) => ({ value: val, count: cnt }));
  }, [baseClaims, groupCol]);

  // Reset selectedGroupVal if a top-level exclusion removes the active group option
  useEffect(() => {
    if (
      selectedGroupVal !== 'ALL' &&
      !availableGroupValues.some((g) => g.value === selectedGroupVal)
    ) {
      setSelectedGroupVal('ALL');
    }
  }, [availableGroupValues, selectedGroupVal]);

  // Reset selectedIssueReason if a top-level exclusion removes the active issue reason
  useEffect(() => {
    if (
      selectedIssueReason !== 'ALL' &&
      !baseIssueSummary.some((iss) => iss.issue_reason === selectedIssueReason)
    ) {
      setSelectedIssueReason('ALL');
    }
  }, [baseIssueSummary, selectedIssueReason]);

  const filteredPreFilingClaims = useMemo(() => {
    if (selectedGroupVal === 'ALL') return baseClaims;
    return baseClaims.filter((c) => c[groupCol] === selectedGroupVal);
  }, [baseClaims, selectedGroupVal, groupCol]);

  const kpis = useMemo(() => {
    const nCoded = filteredPreFilingClaims.length;
    const nExact = filteredPreFilingClaims.filter((c) => c.is_exact_match === 1).length;
    const nPrimary = filteredPreFilingClaims.filter((c) => c.is_match_primary === 1).length;
    const nFollowed = filteredPreFilingClaims.filter((c) =>
      [
        '1. Exact Sequence Match',
        '2. Same Codes (Different Order)',
        '3. Subset of Revedy Codes',
      ].includes(c.icd_match_category)
    ).length;

    const appScores = filteredPreFilingClaims
      .map((c) => c.approval_score)
      .filter((s) => typeof s === 'number' && !Number.isNaN(s));
    const avgApproval =
      appScores.length > 0
        ? (appScores.reduce((acc, v) => acc + v, 0) / appScores.length).toFixed(1)
        : '—';
    const highAppCount = appScores.filter((s) => s >= 80).length;
    const highAppPct =
      appScores.length > 0 ? ((highAppCount / appScores.length) * 100).toFixed(1) : '0.0';

    const dxScores = filteredPreFilingClaims
      .map((c) => c.dx_review_score)
      .filter((s) => typeof s === 'number' && !Number.isNaN(s));
    const avgDxReview =
      dxScores.length > 0
        ? (dxScores.reduce((acc, v) => acc + v, 0) / dxScores.length).toFixed(1)
        : '—';

    return {
      revedyCoded: nCoded,
      exactCount: nExact,
      exactPct: nCoded > 0 ? ((nExact / nCoded) * 100).toFixed(1) : '0.0',
      primaryCount: nPrimary,
      primaryPct: nCoded > 0 ? ((nPrimary / nCoded) * 100).toFixed(1) : '0.0',
      followedCount: nFollowed,
      followedPct: nCoded > 0 ? ((nFollowed / nCoded) * 100).toFixed(1) : '0.0',
      diffCount: Math.max(0, nCoded - nExact),
      avgApproval,
      highAppCount,
      highAppPct,
      avgDxReview,
    };
  }, [filteredPreFilingClaims]);

  const overallTrendData = useMemo(() => {
    const map = {};
    filteredPreFilingClaims.forEach((c) => {
      const bkt = c[bucketCol];
      if (!bkt) return;
      if (!map[bkt]) {
        map[bkt] = { bucket: bkt, claims: 0, exact: 0, primary: 0 };
      }
      map[bkt].claims += 1;
      if (c.is_exact_match === 1) map[bkt].exact += 1;
      if (c.is_match_primary === 1) map[bkt].primary += 1;
    });
    return Object.values(map)
      .sort((a, b) => a.bucket.localeCompare(b.bucket))
      .map((d) => ({
        bucket: d.bucket,
        Claims: d.claims,
        ExactMatchPct: d.claims > 0 ? Number(((d.exact / d.claims) * 100).toFixed(1)) : 0,
        MatchPrimaryPct: d.claims > 0 ? Number(((d.primary / d.claims) * 100).toFixed(1)) : 0,
      }));
  }, [filteredPreFilingClaims, bucketCol]);

  const groupBarData = useMemo(() => {
    const map = {};
    filteredPreFilingClaims.forEach((c) => {
      const g = c[groupCol];
      if (!g) return;
      if (!map[g]) {
        map[g] = { group: g, claims: 0, exact: 0, primary: 0 };
      }
      map[g].claims += 1;
      if (c.is_exact_match === 1) map[g].exact += 1;
      if (c.is_match_primary === 1) map[g].primary += 1;
    });
    return Object.values(map)
      .sort((a, b) => b.claims - a.claims)
      .slice(0, 12)
      .map((d) => ({
        groupLabel: `${d.group} (n=${d.claims})`,
        groupName: d.group,
        Claims: d.claims,
        ExactMatchPct: d.claims > 0 ? Number(((d.exact / d.claims) * 100).toFixed(1)) : 0,
        MatchPrimaryPct: d.claims > 0 ? Number(((d.primary / d.claims) * 100).toFixed(1)) : 0,
      }));
  }, [filteredPreFilingClaims, groupCol]);

  const smallMultiplesData = useMemo(() => {
    const topGroups = groupBarData.slice(0, 9).map((g) => g.groupName);
    return topGroups.map((grpName) => {
      const grpClaims = filteredPreFilingClaims.filter((c) => c[groupCol] === grpName);
      const bktMap = {};
      grpClaims.forEach((c) => {
        const bkt = c[bucketCol];
        if (!bkt) return;
        if (!bktMap[bkt]) {
          bktMap[bkt] = { bucket: bkt, claims: 0, exact: 0, primary: 0 };
        }
        bktMap[bkt].claims += 1;
        if (c.is_exact_match === 1) bktMap[bkt].exact += 1;
        if (c.is_match_primary === 1) bktMap[bkt].primary += 1;
      });
      const series = Object.values(bktMap)
        .sort((a, b) => a.bucket.localeCompare(b.bucket))
        .map((d) => ({
          bucket: d.bucket,
          Claims: d.claims,
          ExactMatchPct: d.claims > 0 ? Number(((d.exact / d.claims) * 100).toFixed(1)) : 0,
          MatchPrimaryPct: d.claims > 0 ? Number(((d.primary / d.claims) * 100).toFixed(1)) : 0,
        }));
      const nTot = grpClaims.length;
      const nEx = grpClaims.filter((c) => c.is_exact_match === 1).length;
      const nPr = grpClaims.filter((c) => c.is_match_primary === 1).length;
      return {
        groupName: grpName,
        totalClaims: nTot,
        exactPct: nTot > 0 ? ((nEx / nTot) * 100).toFixed(1) : '0.0',
        primaryPct: nTot > 0 ? ((nPr / nTot) * 100).toFixed(1) : '0.0',
        series,
      };
    });
  }, [groupBarData, filteredPreFilingClaims, groupCol, bucketCol]);

  const { recentDiffRows, totalMatchingDiffs } = useMemo(() => {
    let rows = [...filteredPreFilingClaims];
    if (diffFilterMode === 'any_diff') {
      rows = rows.filter((c) => c.is_exact_match !== 1);
    } else if (diffFilterMode === 'primary_diff') {
      rows = rows.filter((c) => c.is_match_primary !== 1);
    } else if (diffFilterMode === 'secondary_diff_only') {
      rows = rows.filter((c) => c.is_exact_match !== 1 && c.is_match_primary === 1);
    } else if (diffFilterMode === 'added_non_revedy') {
      rows = rows.filter((c) =>
        [
          '4. Superset of Revedy Codes',
          '5. Partial Overlap',
          '6. Zero Overlap (Replaced Revedy)',
        ].includes(c.icd_match_category)
      );
    }

    if (primaryMatchFilter === 'mismatch') {
      rows = rows.filter((c) => c.is_match_primary !== 1);
    } else if (primaryMatchFilter === 'match') {
      rows = rows.filter((c) => c.is_match_primary === 1);
    } else if (primaryMatchFilter === 'secondary_as_primary') {
      rows = rows.filter(
        (c) => c.primary_icd_match_status === '2. Used Revedy Secondary as Primary'
      );
    } else if (primaryMatchFilter === 'non_revedy_primary') {
      rows = rows.filter((c) => c.primary_icd_match_status === '3. Non-Revedy Primary Dx');
    }

    if (approvalScoreFilter !== 'ALL') {
      rows = rows.filter((c) => {
        const s = c.approval_score;
        if (s == null) return false;
        if (approvalScoreFilter === 'gte_80') return s >= 80;
        if (approvalScoreFilter === '60_79') return s >= 60 && s < 80;
        if (approvalScoreFilter === 'lt_60') return s < 60;
        if (approvalScoreFilter === 'lt_30') return s < 30;
        return true;
      });
    }

    if (needsReviewScoreFilter !== 'ALL') {
      rows = rows.filter((c) => {
        const s = c.needs_review_score;
        if (s == null) return false;
        if (needsReviewScoreFilter === 'gte_80') return s >= 80;
        if (needsReviewScoreFilter === 'gte_60') return s >= 60;
        if (needsReviewScoreFilter === '40_79') return s >= 40 && s < 80;
        if (needsReviewScoreFilter === 'lt_40') return s < 40;
        return true;
      });
    }

    if (dxReviewScoreFilter !== 'ALL') {
      rows = rows.filter((c) => {
        const s = c.dx_review_score;
        if (s == null) return false;
        if (dxReviewScoreFilter === 'gte_80') return s >= 80;
        if (dxReviewScoreFilter === 'gte_60') return s >= 60;
        if (dxReviewScoreFilter === '40_79') return s >= 40 && s < 80;
        if (dxReviewScoreFilter === 'lt_40') return s < 40;
        return true;
      });
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      rows = rows.filter(
        (c) =>
          (c.pid || '').toLowerCase().includes(q) ||
          (c.biller_team || '').toLowerCase().includes(q) ||
          (c.biller_employee || '').toLowerCase().includes(q) ||
          (c.region_org || '').toLowerCase().includes(q) ||
          (c.payer_group || '').toLowerCase().includes(q) ||
          (c.carrier || '').toLowerCase().includes(q) ||
          (c.procedure_name || '').toLowerCase().includes(q) ||
          (c.revedy_icds_dotted || '').toLowerCase().includes(q) ||
          (c.submitted_icds_dotted || '').toLowerCase().includes(q) ||
          (c.icds_added_by_usn || '').toLowerCase().includes(q)
      );
    }

    rows.sort((a, b) => {
      const dCmp = (b.dos || '').localeCompare(a.dos || '');
      if (dCmp !== 0) return dCmp;
      const fCmp = (b.filing_date || '').localeCompare(a.filing_date || '');
      if (fCmp !== 0) return fCmp;
      return (b.pid || '').localeCompare(a.pid || '');
    });

    return {
      totalMatchingDiffs: rows.length,
      recentDiffRows: rows.slice(0, recentLimit),
    };
  }, [
    filteredPreFilingClaims,
    diffFilterMode,
    primaryMatchFilter,
    approvalScoreFilter,
    needsReviewScoreFilter,
    dxReviewScoreFilter,
    searchQuery,
    recentLimit,
  ]);

  const exportPreFilingDiffsToCSV = () => {
    if (!recentDiffRows.length) return;
    const headers = [
      'PID',
      'ClaimID',
      'DOS',
      'Filing_Date',
      'Revedy_ICD_Coded_At',
      'Biller_Team',
      'Biller_Employee',
      'Region_Org',
      'Payer_Group',
      'Carrier',
      'Insurance_Type',
      'Procedure',
      'Approval_Score',
      'Needs_Review_Score',
      'Dx_Review_Score',
      'Exact_Match',
      'Match_Primary',
      'ICD10_Match_Category',
      'Revedy_Primary_Dx',
      'Biller_Primary_Dx',
      'Revedy_Coded_ICD10s',
      'Biller_Submitted_ICD10s',
      'ICD10s_Added_By_Biller',
      'ICD10s_Dropped_By_Biller',
    ];
    const rows = recentDiffRows.map((r) => [
      r.pid,
      r.claim_id,
      r.dos,
      r.filing_date,
      r.revedy_icd_coded_at,
      r.biller_team,
      r.biller_employee,
      r.region_org,
      r.payer_group,
      r.carrier,
      r.insurance_type,
      r.procedure_name,
      r.approval_score != null ? r.approval_score : '',
      r.needs_review_score != null ? r.needs_review_score : '',
      r.dx_review_score != null ? r.dx_review_score : '',
      r.is_exact_match === 1 ? 'Yes' : 'No',
      r.is_match_primary === 1 ? 'Yes' : 'No',
      r.icd_match_category,
      r.revedy_primary_icd,
      r.submitted_primary_icd,
      r.revedy_icds_dotted,
      r.submitted_icds_dotted,
      r.icds_added_by_usn,
      r.icds_dropped_by_usn,
    ]);
    downloadCSVFile(headers, rows, `usn_pre_filing_icd10_differences_${recentDiffRows.length}`);
  };

  // ============================================================================
  // VIEW 2: Cases Filed Prior to Revedy Coding (`baseFiledPriorClaims`)
  // ============================================================================
  const postFilingStats = useMemo(() => {
    const nTotal = baseFiledPriorClaims.length;
    const nUploadedAfter = baseFiledPriorClaims.filter(
      (c) => c.timing_sub_status === 'Case Uploaded After Filing Date'
    ).length;
    const nCodedAfter = nTotal - nUploadedAfter;
    const nExact = baseFiledPriorClaims.filter((c) => c.is_exact_match === 1).length;
    const nPrimary = baseFiledPriorClaims.filter((c) => c.is_match_primary === 1).length;
    const validDays = baseFiledPriorClaims
      .map((c) => c.days_filed_before_coding)
      .filter((d) => typeof d === 'number' && !Number.isNaN(d));
    const avgDays =
      validDays.length > 0
        ? (validDays.reduce((acc, v) => acc + v, 0) / validDays.length).toFixed(1)
        : '0.0';

    const byGroupMap = {};
    baseFiledPriorClaims.forEach((c) => {
      const g = c[groupCol] || 'Unknown';
      if (!byGroupMap[g]) {
        byGroupMap[g] = {
          group: g,
          total: 0,
          uploadedAfter: 0,
          codedAfter: 0,
          exact: 0,
          primary: 0,
        };
      }
      byGroupMap[g].total += 1;
      if (c.timing_sub_status === 'Case Uploaded After Filing Date') {
        byGroupMap[g].uploadedAfter += 1;
      } else {
        byGroupMap[g].codedAfter += 1;
      }
      if (c.is_exact_match === 1) byGroupMap[g].exact += 1;
      if (c.is_match_primary === 1) byGroupMap[g].primary += 1;
    });

    const groupBreakdown = Object.values(byGroupMap)
      .sort((a, b) => b.total - a.total)
      .map((d) => ({
        ...d,
        exactPct: d.total > 0 ? ((d.exact / d.total) * 100).toFixed(1) : '0.0',
        primaryPct: d.total > 0 ? ((d.primary / d.total) * 100).toFixed(1) : '0.0',
      }));

    return {
      total: nTotal,
      uploadedAfter: nUploadedAfter,
      codedAfter: nCodedAfter,
      avgDays,
      exactCount: nExact,
      exactPct: nTotal > 0 ? ((nExact / nTotal) * 100).toFixed(1) : '0.0',
      primaryCount: nPrimary,
      primaryPct: nTotal > 0 ? ((nPrimary / nTotal) * 100).toFixed(1) : '0.0',
      groupBreakdown,
    };
  }, [baseFiledPriorClaims, groupCol]);

  const { filteredPostFilingRows, totalMatchingPostFiling } = useMemo(() => {
    let rows = [...baseFiledPriorClaims];
    if (postFilingSubFilter !== 'ALL') {
      rows = rows.filter((c) => c.timing_sub_status === postFilingSubFilter);
    }
    if (postFilingSearch.trim()) {
      const q = postFilingSearch.trim().toLowerCase();
      rows = rows.filter(
        (c) =>
          (c.pid || '').toLowerCase().includes(q) ||
          (c.biller_team || '').toLowerCase().includes(q) ||
          (c.biller_employee || '').toLowerCase().includes(q) ||
          (c.region_org || '').toLowerCase().includes(q) ||
          (c.carrier || '').toLowerCase().includes(q) ||
          (c.procedure_name || '').toLowerCase().includes(q) ||
          (c.revedy_icds_dotted || '').toLowerCase().includes(q) ||
          (c.submitted_icds_dotted || '').toLowerCase().includes(q)
      );
    }
    rows.sort((a, b) => {
      const dCmp = (b.dos || '').localeCompare(a.dos || '');
      if (dCmp !== 0) return dCmp;
      return (b.filing_date || '').localeCompare(a.filing_date || '');
    });
    return {
      totalMatchingPostFiling: rows.length,
      filteredPostFilingRows: rows.slice(0, postFilingLimit),
    };
  }, [baseFiledPriorClaims, postFilingSubFilter, postFilingSearch, postFilingLimit]);

  const exportPostFilingToCSV = () => {
    if (!filteredPostFilingRows.length) return;
    const headers = [
      'PID',
      'ClaimID',
      'DOS',
      'Filing_Date',
      'Revedy_Case_Created_At',
      'Revedy_ICD_Coded_At',
      'Days_Filed_Before_Coding',
      'Timing_Sub_Status',
      'Biller_Team',
      'Biller_Employee',
      'Region_Org',
      'Payer_Group',
      'Carrier',
      'Procedure',
      'Approval_Score',
      'Needs_Review_Score',
      'Dx_Review_Score',
      'PostHoc_Exact_Match',
      'PostHoc_Match_Primary',
      'ICD10_Match_Category',
      'Revedy_Coded_ICD10s',
      'Biller_Submitted_ICD10s',
      'ICD10s_Added_By_Biller',
      'ICD10s_Dropped_By_Biller',
    ];
    const rows = filteredPostFilingRows.map((r) => [
      r.pid,
      r.claim_id,
      r.dos,
      r.filing_date,
      r.revedy_case_created_at,
      r.revedy_icd_coded_at,
      r.days_filed_before_coding,
      r.timing_sub_status,
      r.biller_team,
      r.biller_employee,
      r.region_org,
      r.payer_group,
      r.carrier,
      r.procedure_name,
      r.approval_score != null ? r.approval_score : '',
      r.needs_review_score != null ? r.needs_review_score : '',
      r.dx_review_score != null ? r.dx_review_score : '',
      r.is_exact_match === 1 ? 'Yes' : 'No',
      r.is_match_primary === 1 ? 'Yes' : 'No',
      r.icd_match_category,
      r.revedy_icds_dotted,
      r.submitted_icds_dotted,
      r.icds_added_by_usn,
      r.icds_dropped_by_usn,
    ]);
    downloadCSVFile(
      headers,
      rows,
      `usn_cases_filed_prior_to_revedy_coding_${filteredPostFilingRows.length}`
    );
  };

  // ============================================================================
  // VIEW 3: Excluded Claims & Data Issues (`baseExcludedClaims` + `baseIssueSummary`)
  // ============================================================================
  const excludedStats = useMemo(() => {
    const nTotal = baseExcludedClaims.length;
    const nCsvIssues = baseExcludedClaims.filter((c) => c.has_csv_issue === 1).length;
    const nCaseIssues = baseExcludedClaims.filter((c) => c.has_case_data_issue === 1).length;
    return {
      totalExcluded: nTotal,
      csvIssuesCount: nCsvIssues,
      caseIssuesCount: nCaseIssues,
    };
  }, [baseExcludedClaims]);

  const { filteredExcludedRows, totalMatchingExcluded } = useMemo(() => {
    let rows = [...baseExcludedClaims];
    if (selectedIssueReason !== 'ALL') {
      rows = rows.filter((c) =>
        Array.isArray(c.issue_reasons)
          ? c.issue_reasons.includes(selectedIssueReason)
          : c.primary_issue === selectedIssueReason
      );
    }
    if (excludedSearch.trim()) {
      const q = excludedSearch.trim().toLowerCase();
      rows = rows.filter(
        (c) =>
          (c.pid || '').toLowerCase().includes(q) ||
          (c.claim_id || '').toLowerCase().includes(q) ||
          (c.biller_employee || '').toLowerCase().includes(q) ||
          (c.biller_team || '').toLowerCase().includes(q) ||
          (c.carrier || '').toLowerCase().includes(q) ||
          (c.region_org || '').toLowerCase().includes(q) ||
          (c.issue_reasons_str || '').toLowerCase().includes(q)
      );
    }
    rows.sort((a, b) => (b.dos || '').localeCompare(a.dos || ''));
    return {
      totalMatchingExcluded: rows.length,
      filteredExcludedRows: rows.slice(0, excludedLimit),
    };
  }, [baseExcludedClaims, selectedIssueReason, excludedSearch, excludedLimit]);

  const exportExcludedToCSV = () => {
    if (!filteredExcludedRows.length) return;
    const headers = [
      'PID',
      'ClaimID',
      'DOS',
      'Filing_Date',
      'Primary_Issue',
      'All_Issue_Reasons',
      'Biller_Employee',
      'Biller_Team',
      'Region_Org',
      'Carrier',
      'Insurance_Type',
      'Procedure_Type',
      'Procedure_Name',
      'Submitted_ICD10s',
      'In_Revedy_DB',
      'Has_Revedy_ICD10',
    ];
    const rows = filteredExcludedRows.map((r) => [
      r.pid,
      r.claim_id,
      r.dos,
      r.filing_date,
      r.primary_issue,
      r.issue_reasons_str,
      r.biller_employee,
      r.biller_team,
      r.region_org,
      r.carrier,
      r.insurance_type,
      r.procedure_type,
      r.procedure_name,
      r.submitted_icds_dotted,
      r.in_revedy_db === 1 ? 'Yes' : 'No',
      r.has_revedy_icd === 1 ? 'Yes' : 'No',
    ]);
    downloadCSVFile(
      headers,
      rows,
      `usn_excluded_claims_data_issues_${filteredExcludedRows.length}`
    );
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" py={12}>
        <Spinner size="xl" color="blue.500" mr={3} />
        <Text color="gray.600">Loading USN Biller ICD-10 Compliance dataset...</Text>
      </Flex>
    );
  }

  return (
    <Box>
      {/* Top File Upload & Rebuild Bar */}
      <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px" mb={4}>
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
          <Box>
            <HStack spacing={2} mb={1}>
              <Heading size="sm" color="gray.800">
                USMON Billing Report Source (`org_data_file_uploads`)
              </Heading>
              <Badge colorScheme="purple">
                file_type: {fileMeta?.file_type || 'usn_usmon_billing_report'}
              </Badge>
              {fileMeta?.org_id != null && (
                <Badge colorScheme="blue">org_id: {fileMeta.org_id}</Badge>
              )}
              <Badge colorScheme="green">Zero Fallbacks / Strict Validation</Badge>
            </HStack>
            <Text fontSize="xs" color="gray.600">
              <strong>Active File:</strong> {fileMeta?.filename || 'None'} |{' '}
              <strong>Raw Rows Stored:</strong> {(fileMeta?.row_count || 0).toLocaleString()} |{' '}
              <strong>Total Billed Claims (DOS &ge; 2026-08-01):</strong>{' '}
              {(fileMeta?.record_count || 0).toLocaleString()} |{' '}
              <strong>Pre-Filing Valid:</strong> {claims.length.toLocaleString()} |{' '}
              <strong>Filed Prior to Revedy Coding:</strong>{' '}
              {filedPriorClaims.length.toLocaleString()} |{' '}
              <strong>Excluded (Data Issues):</strong> {excludedClaims.length.toLocaleString()} |{' '}
              <strong>Rebuilt At:</strong>{' '}
              {fileMeta?.rebuilt_at ? new Date(fileMeta.rebuilt_at).toLocaleString() : 'N/A'}
            </Text>
          </Box>
          <HStack spacing={3}>
            <input
              type="file"
              accept=".csv,.xlsx"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileUpload}
            />
            <Button
              size="sm"
              colorScheme="blue"
              isLoading={uploading}
              loadingText="Validating & Uploading..."
              onClick={() => fileInputRef.current?.click()}
            >
              Upload New Billing Report (.csv / .xlsx)
            </Button>
            <Button
              size="sm"
              variant="outline"
              colorScheme="teal"
              leftIcon={<RepeatIcon />}
              isLoading={rebuilding}
              loadingText="Rebuilding..."
              onClick={handleRebuild}
            >
              Rebuild from Stored CSV &amp; Revedy DB
            </Button>
          </HStack>
        </Flex>
      </Box>

      {error && (
        <Alert status="error" borderRadius="md" mb={4}>
          <AlertIcon />
          <Box fontSize="sm">{error}</Box>
        </Alert>
      )}

      {statusMsg && (
        <Alert status="success" borderRadius="md" mb={4}>
          <AlertIcon />
          <Box fontSize="sm">{statusMsg}</Box>
        </Alert>
      )}

      {/* Top-Level Overlays (Applied First: CMS-1500 Submitted Date, Biller Team, Payer Group) */}
      {(allBillerTeamOptions.length > 0 || allPayerGroupOptions.length > 0) && (
        <Box
          bg={hasAnyTopLevelOverlay ? 'orange.50' : 'white'}
          p={4}
          borderRadius="md"
          shadow="sm"
          borderWidth="1px"
          borderColor={hasAnyTopLevelOverlay ? 'orange.300' : 'gray.200'}
          mb={4}
        >
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={2} mb={3}>
            <HStack spacing={2} flexWrap="wrap">
              <Heading size="xs" color="gray.800" textTransform="uppercase" letterSpacing="wide">
                Top-Level Dataset Overlays (Applied First Across All Views)
              </Heading>
              {!hasAnyTopLevelOverlay ? (
                <Badge colorScheme="green" fontSize="10px">
                  All Data Included ({rawTotalClaimsCount.toLocaleString()} total claims |{' '}
                  {claims.length.toLocaleString()} pre-filing valid)
                </Badge>
              ) : (
                <Badge colorScheme="red" fontSize="10px">
                  Active Overlays ({hiddenByOverlayCount.toLocaleString()} claims hidden |{' '}
                  {baseTotalClaimsCount.toLocaleString()} total active |{' '}
                  {baseClaims.length.toLocaleString()} pre-filing valid)
                </Badge>
              )}
            </HStack>
            {hasAnyTopLevelOverlay && (
              <Button
                size="xs"
                colorScheme="orange"
                variant="solid"
                onClick={() => {
                  setExcludedBillerTeams([]);
                  setExcludedPayerGroups([]);
                  setFilingStartDate('');
                  setFilingEndDate('');
                }}
              >
                Reset All Top-Level Overlays
              </Button>
            )}
          </Flex>

          <VStack align="stretch" spacing={3}>
            {/* 1. CMS-1500 Submitted Date (Billing Date) Range Overlay */}
            <Box
              p={2.5}
              bg="white"
              borderRadius="md"
              borderWidth="1px"
              borderColor={filingStartDate || filingEndDate ? 'blue.300' : 'gray.100'}
            >
              <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
                <HStack spacing={3} flexWrap="wrap">
                  <Text fontSize="xs" fontWeight="bold" color="gray.700">
                    1. CMS-1500 Submitted Date (`Billing Date`):
                  </Text>
                  <HStack spacing={1}>
                    <Text fontSize="xs" color="gray.600">
                      Start Date:
                    </Text>
                    <Input
                      type="date"
                      size="xs"
                      w="135px"
                      value={filingStartDate}
                      onChange={(e) => setFilingStartDate(e.target.value)}
                    />
                  </HStack>
                  <HStack spacing={1}>
                    <Text fontSize="xs" color="gray.600">
                      End Date:
                    </Text>
                    <Input
                      type="date"
                      size="xs"
                      w="135px"
                      value={filingEndDate}
                      onChange={(e) => setFilingEndDate(e.target.value)}
                    />
                  </HStack>
                  {filingDateBounds.minDate && (
                    <Badge colorScheme="gray" fontSize="10px">
                      File Range: {filingDateBounds.minDate} &rarr; {filingDateBounds.maxDate}
                    </Badge>
                  )}
                </HStack>
                <HStack spacing={1.5} flexWrap="wrap">
                  <Text fontSize="10px" color="gray.500" fontWeight="semibold">
                    Quick Start:
                  </Text>
                  {['2026-09-01', '2026-09-15', '2026-10-01', '2026-10-15'].map((preset) => (
                    <Button
                      key={preset}
                      size="xs"
                      h="22px"
                      fontSize="10px"
                      colorScheme={filingStartDate === preset && !filingEndDate ? 'blue' : 'gray'}
                      variant={filingStartDate === preset && !filingEndDate ? 'solid' : 'outline'}
                      onClick={() => {
                        setFilingStartDate(preset);
                        setFilingEndDate('');
                      }}
                    >
                      &ge; {preset}
                    </Button>
                  ))}
                  {(filingStartDate || filingEndDate) && (
                    <Button
                      size="xs"
                      h="22px"
                      fontSize="10px"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => {
                        setFilingStartDate('');
                        setFilingEndDate('');
                      }}
                    >
                      Clear Dates
                    </Button>
                  )}
                </HStack>
              </Flex>
            </Box>

            {/* 2. Biller Team Exclusion / Inclusion Overlay */}
            <Box
              p={2.5}
              bg="white"
              borderRadius="md"
              borderWidth="1px"
              borderColor={excludedBillerTeams.length > 0 ? 'orange.300' : 'gray.100'}
            >
              <Flex justify="space-between" align="center" flexWrap="wrap" gap={2} mb={1.5}>
                <HStack spacing={2}>
                  <Text fontSize="xs" fontWeight="bold" color="gray.700">
                    2. Exclude / Include by Biller Team:
                  </Text>
                  <Text fontSize="11px" color="gray.500">
                    (Click any team to exclude/include)
                  </Text>
                </HStack>
                {excludedBillerTeams.length > 0 && (
                  <Button
                    size="xs"
                    h="20px"
                    fontSize="10px"
                    colorScheme="orange"
                    variant="outline"
                    onClick={() => setExcludedBillerTeams([])}
                  >
                    Include All Teams ({allBillerTeamOptions.length})
                  </Button>
                )}
              </Flex>
              <HStack spacing={2} flexWrap="wrap">
                {allBillerTeamOptions.map((item) => {
                  const isExcluded = excludedBillerTeams.includes(item.teamKey);
                  return (
                    <Button
                      key={item.teamKey}
                      size="xs"
                      colorScheme={isExcluded ? 'red' : 'purple'}
                      variant={isExcluded ? 'outline' : 'solid'}
                      onClick={() => toggleExcludeBillerTeam(item.teamKey)}
                      textDecoration={isExcluded ? 'line-through' : 'none'}
                      opacity={isExcluded ? 0.75 : 1}
                      title={
                        isExcluded
                          ? `Click to re-include ${item.teamKey}`
                          : `Click to exclude ${item.teamKey} (${item.preFilingCount.toLocaleString()} pre-filing valid / ${item.totalCount.toLocaleString()} total claims)`
                      }
                    >
                      {isExcluded ? '✕ ' : '✓ '}
                      {item.teamKey} ({item.preFilingCount.toLocaleString()})
                    </Button>
                  );
                })}
              </HStack>
            </Box>

            {/* 3. Payer Group Exclusion / Inclusion Overlay */}
            <Box
              p={2.5}
              bg="white"
              borderRadius="md"
              borderWidth="1px"
              borderColor={excludedPayerGroups.length > 0 ? 'orange.300' : 'gray.100'}
            >
              <Flex justify="space-between" align="center" flexWrap="wrap" gap={2} mb={1.5}>
                <HStack spacing={2}>
                  <Text fontSize="xs" fontWeight="bold" color="gray.700">
                    3. Exclude / Include by Payer Group:
                  </Text>
                  <Text fontSize="11px" color="gray.500">
                    (Click any payer group to exclude/include)
                  </Text>
                </HStack>
                {excludedPayerGroups.length > 0 && (
                  <Button
                    size="xs"
                    h="20px"
                    fontSize="10px"
                    colorScheme="orange"
                    variant="outline"
                    onClick={() => setExcludedPayerGroups([])}
                  >
                    Include All Payer Groups ({allPayerGroupOptions.length})
                  </Button>
                )}
              </Flex>
              <HStack spacing={2} flexWrap="wrap">
                {allPayerGroupOptions.map((item) => {
                  const isExcluded = excludedPayerGroups.includes(item.payerGroupKey);
                  return (
                    <Button
                      key={item.payerGroupKey}
                      size="xs"
                      colorScheme={isExcluded ? 'red' : 'teal'}
                      variant={isExcluded ? 'outline' : 'solid'}
                      onClick={() => toggleExcludePayerGroup(item.payerGroupKey)}
                      textDecoration={isExcluded ? 'line-through' : 'none'}
                      opacity={isExcluded ? 0.75 : 1}
                      title={
                        isExcluded
                          ? `Click to re-include ${item.payerGroupKey}`
                          : `Click to exclude ${item.payerGroupKey} (${item.preFilingCount.toLocaleString()} pre-filing valid / ${item.totalCount.toLocaleString()} total claims)`
                      }
                    >
                      {isExcluded ? '✕ ' : '✓ '}
                      {item.payerGroupKey} ({item.preFilingCount.toLocaleString()})
                    </Button>
                  );
                })}
              </HStack>
            </Box>
          </VStack>
        </Box>
      )}

      {/* 3-Report Selector Bar */}
      <HStack spacing={3} mb={6} flexWrap="wrap">
        <Button
          size="sm"
          colorScheme={activeView === 'pre_filing_compliance' ? 'blue' : 'gray'}
          variant={activeView === 'pre_filing_compliance' ? 'solid' : 'outline'}
          onClick={() => setActiveView('pre_filing_compliance')}
        >
          1. Biller ICD-10 Compliance (Coded Prior to Filing){' '}
          <Badge ml={2} colorScheme="green">
            {baseClaims.length.toLocaleString()}
          </Badge>
        </Button>
        <Button
          size="sm"
          colorScheme={activeView === 'filed_prior_to_coding' ? 'orange' : 'gray'}
          variant={activeView === 'filed_prior_to_coding' ? 'solid' : 'outline'}
          onClick={() => setActiveView('filed_prior_to_coding')}
        >
          2. Cases Filed Prior to Revedy Coding{' '}
          <Badge ml={2} colorScheme="orange">
            {baseFiledPriorClaims.length.toLocaleString()}
          </Badge>
        </Button>
        <Button
          size="sm"
          colorScheme={activeView === 'excluded_issues' ? 'red' : 'gray'}
          variant={activeView === 'excluded_issues' ? 'solid' : 'outline'}
          onClick={() => setActiveView('excluded_issues')}
        >
          3. Excluded Claims &amp; Data Issues Report{' '}
          <Badge ml={2} colorScheme="red">
            {baseExcludedClaims.length.toLocaleString()}
          </Badge>
        </Button>
      </HStack>

      {/* ==========================================================================
          VIEW 1: PRE-FILING BILLER COMPLIANCE ANALYSIS (DEFAULT)
         ========================================================================== */}
      {activeView === 'pre_filing_compliance' && (
        <Box>
          {/* Filter Controls */}
          <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px" mb={6}>
            <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                  1. Group By
                </Text>
                <Select
                  size="sm"
                  value={groupCol}
                  onChange={(e) => {
                    setGroupCol(e.target.value);
                    setSelectedGroupVal('ALL');
                  }}
                >
                  {GROUP_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                  2. Filter Specific Group
                </Text>
                <Select
                  size="sm"
                  value={selectedGroupVal}
                  onChange={(e) => setSelectedGroupVal(e.target.value)}
                >
                  <option value="ALL">
                    All Active Pre-Filing Claims ({baseClaims.length.toLocaleString()})
                  </option>
                  {availableGroupValues.map((g) => (
                    <option key={g.value} value={g.value}>
                      {g.value} ({g.count.toLocaleString()})
                    </option>
                  ))}
                </Select>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                  3. Trend Time Axis
                </Text>
                <Select size="sm" value={timeAxis} onChange={(e) => setTimeAxis(e.target.value)}>
                  <option value="dos">Date of Surgery (DOS)</option>
                  <option value="filing_date">CMS-1500 Filing Date</option>
                </Select>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                  4. Time Grain
                </Text>
                <Select size="sm" value={timeGrain} onChange={(e) => setTimeGrain(e.target.value)}>
                  <option value="week">Weekly (Monday Start)</option>
                  <option value="month">Monthly (YYYY-MM)</option>
                </Select>
              </Box>
            </SimpleGrid>
          </Box>

          {/* KPI Cards (Strictly Pre-Filing Coded Claims) */}
          <SimpleGrid columns={{ base: 2, md: 6 }} spacing={4} mb={6}>
            <Box p={4} bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
              <Stat>
                <StatLabel color="gray.500" fontSize="xs">
                  Pre-Filing Coded Claims
                </StatLabel>
                <StatNumber fontSize="2xl">{kpis.revedyCoded.toLocaleString()}</StatNumber>
                <StatHelpText fontSize="xs" mb={0}>
                  Coded &le; Filing Date (Valid Data)
                </StatHelpText>
              </Stat>
            </Box>
            <Box
              p={4}
              bg="white"
              borderRadius="md"
              shadow="sm"
              borderWidth="1px"
              borderLeft="4px solid #059669"
            >
              <Stat>
                <StatLabel color="gray.600" fontSize="xs">
                  Exact Match % (Exact ICD Sequence)
                </StatLabel>
                <StatNumber fontSize="2xl" color="green.600">
                  {kpis.exactPct}%
                </StatNumber>
                <StatHelpText fontSize="xs" mb={0}>
                  {kpis.exactCount.toLocaleString()} exact sequence matches
                </StatHelpText>
              </Stat>
            </Box>
            <Box
              p={4}
              bg="white"
              borderRadius="md"
              shadow="sm"
              borderWidth="1px"
              borderLeft="4px solid #2563EB"
            >
              <Stat>
                <StatLabel color="gray.600" fontSize="xs">
                  Match Primary % (ICD_A == Revedy #1)
                </StatLabel>
                <StatNumber fontSize="2xl" color="blue.600">
                  {kpis.primaryPct}%
                </StatNumber>
                <StatHelpText fontSize="xs" mb={0}>
                  {kpis.primaryCount.toLocaleString()} primary diagnosis matches
                </StatHelpText>
              </Stat>
            </Box>
            <Box p={4} bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
              <Stat>
                <StatLabel color="gray.500" fontSize="xs">
                  ICD-10 Followed (incl. Subset) %
                </StatLabel>
                <StatNumber fontSize="2xl" color="teal.600">
                  {kpis.followedPct}%
                </StatNumber>
                <StatHelpText fontSize="xs" mb={0}>
                  {kpis.followedCount.toLocaleString()} w/ zero external codes
                </StatHelpText>
              </Stat>
            </Box>
            <Box
              p={4}
              bg="white"
              borderRadius="md"
              shadow="sm"
              borderWidth="1px"
              borderLeft="4px solid #DC2626"
            >
              <Stat>
                <StatLabel color="gray.600" fontSize="xs">
                  Claims w/ ICD-10 Differences
                </StatLabel>
                <StatNumber fontSize="2xl" color="red.600">
                  {kpis.diffCount.toLocaleString()}
                </StatNumber>
                <StatHelpText fontSize="xs" mb={0}>
                  Did not exact-match Revedy sequence
                </StatHelpText>
              </Stat>
            </Box>
            <Box
              p={4}
              bg="white"
              borderRadius="md"
              shadow="sm"
              borderWidth="1px"
              borderLeft="4px solid #7C3AED"
            >
              <Stat>
                <StatLabel color="gray.600" fontSize="xs">
                  Avg General Approval Score
                </StatLabel>
                <StatNumber fontSize="2xl" color="purple.600">
                  {kpis.avgApproval}
                </StatNumber>
                <StatHelpText fontSize="xs" mb={0}>
                  &ge;80: {kpis.highAppPct}% | Avg Dx Rev: {kpis.avgDxReview}
                </StatHelpText>
              </Stat>
            </Box>
          </SimpleGrid>

          {/* Top Row Graphs */}
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
            <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px">
              <Heading size="sm" mb={1} color="gray.700">
                1. Overall Exact Match % &amp; Match Primary % Over Time ({bucketCol})
              </Heading>
              <Text fontSize="xs" color="gray.500" mb={3}>
                Strictly claims coded in Revedy prior to Filing Date. Lines show Exact Sequence Match % (Green) and Match Primary Dx % (Blue).
              </Text>
              <Box height="340px">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={overallTrendData}
                    margin={{ top: 10, right: 20, left: 0, bottom: 25 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="bucket" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" />
                    <YAxis
                      yAxisId="left"
                      orientation="left"
                      domain={[0, 100]}
                      unit="%"
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar
                      yAxisId="right"
                      dataKey="Claims"
                      name="Pre-Filing Claims"
                      fill="#CBD5E1"
                      barSize={24}
                    />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="ExactMatchPct"
                      name="Exact Match %"
                      stroke="#059669"
                      strokeWidth={3}
                      dot={{ r: 4 }}
                    />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="MatchPrimaryPct"
                      name="Match Primary % (ICD_A)"
                      stroke="#2563EB"
                      strokeWidth={2.5}
                      strokeDasharray="5 5"
                      dot={{ r: 3 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            </Box>

            <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px">
              <Heading size="sm" mb={1} color="gray.700">
                2. Exact Match % vs. Match Primary % by Group ({groupCol})
              </Heading>
              <Text fontSize="xs" color="gray.500" mb={3}>
                Comparing ICD-10 Exact Sequence Match % and Primary Dx Match % across top groups (zero guessed teams/fields).
              </Text>
              <Box height="340px">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={groupBarData}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 110, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} unit="%" tick={{ fontSize: 11 }} />
                    <YAxis
                      dataKey="groupLabel"
                      type="category"
                      tick={{ fontSize: 11 }}
                      width={140}
                    />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="ExactMatchPct" name="Exact Match %" fill="#059669" />
                    <Bar
                      dataKey="MatchPrimaryPct"
                      name="Match Primary % (ICD_A)"
                      fill="#2563EB"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </SimpleGrid>

          {/* Small-Multiples Grid by Group */}
          {smallMultiplesData.length > 0 && (
            <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px" mb={6}>
              <Heading size="sm" mb={1} color="gray.700">
                3. Small-Multiples by {groupCol}: Exact Match % &amp; Match Primary % Over Time (
                {bucketCol})
              </Heading>
              <Text fontSize="xs" color="gray.500" mb={4}>
                Each panel displays Exact Match % (Green), Match Primary % (Blue), and Pre-Filing Claim Volume bars (Gray).
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                {smallMultiplesData.map((sm, idx) => (
                  <Box key={sm.groupName} p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                    <Flex justify="space-between" align="center" mb={2}>
                      <Text fontSize="xs" fontWeight="bold" color="gray.800" isTruncated>
                        #{idx + 1} {sm.groupName} (n={sm.totalClaims})
                      </Text>
                      <HStack spacing={1}>
                        <Badge colorScheme="green" fontSize="10px">
                          Exact: {sm.exactPct}%
                        </Badge>
                        <Badge colorScheme="blue" fontSize="10px">
                          Prim: {sm.primaryPct}%
                        </Badge>
                      </HStack>
                    </Flex>
                    <Box height="200px" bg="white" p={1} borderRadius="sm">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                          data={sm.series}
                          margin={{ top: 5, right: 10, left: -15, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="bucket" tick={{ fontSize: 9 }} />
                          <YAxis yAxisId="left" domain={[0, 100]} tick={{ fontSize: 9 }} />
                          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 9 }} />
                          <Tooltip />
                          <Bar yAxisId="right" dataKey="Claims" fill="#E2E8F0" barSize={14} />
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="ExactMatchPct"
                            name="Exact Match %"
                            stroke="#059669"
                            strokeWidth={2}
                            dot={{ r: 2 }}
                          />
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="MatchPrimaryPct"
                            name="Match Primary %"
                            stroke="#2563EB"
                            strokeWidth={2}
                            strokeDasharray="4 4"
                            dot={{ r: 2 }}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          )}

          <Divider my={6} />

          {/* Most Recent N Cases Showing ICD-10 Differences */}
          <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px">
            <Flex justify="space-between" align="center" flexWrap="wrap" gap={4} mb={4}>
              <Box>
                <Heading size="md" color="gray.800">
                  Most Recent {recentLimit} Pre-Filing Coded Cases Showing ICD-10 Differences
                </Heading>
                <Text fontSize="xs" color="gray.600">
                  Showing <strong>{recentDiffRows.length.toLocaleString()}</strong> of{' '}
                  <strong>{totalMatchingDiffs.toLocaleString()}</strong> matching pre-filing claims
                  (sorted by most recent DOS &amp; Filing Date descending).
                </Text>
              </Box>
              <Button
                size="sm"
                colorScheme="green"
                onClick={exportPreFilingDiffsToCSV}
                isDisabled={recentDiffRows.length === 0}
              >
                Export These {recentDiffRows.length} Cases to CSV
              </Button>
            </Flex>

            <SimpleGrid columns={{ base: 1, md: 4, lg: 7 }} spacing={3} mb={4}>
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                  Difference Filter
                </Text>
                <Select
                  size="sm"
                  value={diffFilterMode}
                  onChange={(e) => setDiffFilterMode(e.target.value)}
                >
                  {DIFF_FILTER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                  Primary Match Filter
                </Text>
                <Select
                  size="sm"
                  value={primaryMatchFilter}
                  onChange={(e) => setPrimaryMatchFilter(e.target.value)}
                >
                  {PRIMARY_MATCH_FILTER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                  Approval Score Filter
                </Text>
                <Select
                  size="sm"
                  value={approvalScoreFilter}
                  onChange={(e) => setApprovalScoreFilter(e.target.value)}
                >
                  {APPROVAL_SCORE_FILTER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                  Needs Review Score Filter
                </Text>
                <Select
                  size="sm"
                  value={needsReviewScoreFilter}
                  onChange={(e) => setNeedsReviewScoreFilter(e.target.value)}
                >
                  {NEEDS_REVIEW_SCORE_FILTER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                  Dx Review Score Filter
                </Text>
                <Select
                  size="sm"
                  value={dxReviewScoreFilter}
                  onChange={(e) => setDxReviewScoreFilter(e.target.value)}
                >
                  {DX_REVIEW_SCORE_FILTER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                  Max Cases to Display
                </Text>
                <Select
                  size="sm"
                  value={recentLimit}
                  onChange={(e) => setRecentLimit(Number(e.target.value))}
                >
                  <option value={50}>Most Recent 50 Cases</option>
                  <option value={100}>Most Recent 100 Cases</option>
                  <option value={200}>Most Recent 200 Cases</option>
                  <option value={500}>Most Recent 500 Cases</option>
                  <option value={1000}>Most Recent 1,000 Cases</option>
                </Select>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                  Quick Search (PID, Biller, ICD-10, Carrier)
                </Text>
                <Input
                  size="sm"
                  placeholder="e.g. Chandupa or M54.16 or 4008316..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </Box>
            </SimpleGrid>

            <Box overflowX="auto" maxH="650px" overflowY="auto">
              <Table variant="simple" size="sm">
                <Thead position="sticky" top={0} bg="gray.100" zIndex={1}>
                  <Tr>
                    <Th>PID.Claim</Th>
                    <Th>DOS / Filed / Coded</Th>
                    <Th>Biller (Team)</Th>
                    <Th>Region / Carrier</Th>
                    <Th>Procedure</Th>
                    <Th isNumeric>Approval Score</Th>
                    <Th isNumeric>Needs Review</Th>
                    <Th isNumeric>Dx Review</Th>
                    <Th>Exact Match</Th>
                    <Th>Match Primary</Th>
                    <Th>Revedy Coded ICD-10s</Th>
                    <Th>Biller Submitted ICD-10s</Th>
                    <Th>Added by Biller</Th>
                    <Th>Dropped by Biller</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {recentDiffRows.map((row) => (
                    <Tr key={row.claim_key} _hover={{ bg: 'gray.50' }}>
                      <Td fontWeight="bold" whiteSpace="nowrap">
                        <Link
                          href={`/ionm-strict-analysis/${row.pid}`}
                          isExternal
                          color="blue.600"
                        >
                          {row.pid}.{row.claim_id} <ExternalLinkIcon mx="2px" />
                        </Link>
                      </Td>
                      <Td whiteSpace="nowrap" fontSize="xs">
                        <Text fontWeight="medium">DOS: {row.dos}</Text>
                        <Text color="gray.600">Filed: {row.filing_date}</Text>
                        <Text color="teal.700" fontSize="10px">
                          Coded: {row.revedy_icd_coded_at}
                        </Text>
                      </Td>
                      <Td fontSize="xs">
                        <Text fontWeight="semibold">{row.biller_employee}</Text>
                        <Badge colorScheme="purple" fontSize="10px">
                          {row.biller_team}
                        </Badge>
                      </Td>
                      <Td fontSize="xs">
                        <Text fontWeight="medium">{row.region_org}</Text>
                        <Text color="gray.500" maxW="140px" isTruncated title={row.carrier}>
                          {row.carrier}
                        </Text>
                        {row.payer_group && (
                          <Badge colorScheme="teal" variant="subtle" fontSize="9px">
                            {row.payer_group}
                          </Badge>
                        )}
                      </Td>
                      <Td fontSize="xs" maxW="160px" isTruncated title={row.procedure_name}>
                        {row.procedure_name}
                      </Td>
                      <Td isNumeric>
                        {row.approval_score != null ? (
                          <Badge
                            colorScheme={
                              row.approval_score >= 80
                                ? 'green'
                                : row.approval_score >= 60
                                ? 'yellow'
                                : 'red'
                            }
                            fontSize="xs"
                          >
                            {row.approval_score}
                          </Badge>
                        ) : (
                          <Text fontSize="xs" color="gray.400">
                            —
                          </Text>
                        )}
                      </Td>
                      <Td isNumeric>
                        {row.needs_review_score != null ? (
                          <Badge
                            colorScheme={
                              row.needs_review_score >= 80
                                ? 'red'
                                : row.needs_review_score >= 40
                                ? 'yellow'
                                : 'green'
                            }
                            fontSize="xs"
                          >
                            {row.needs_review_score}
                          </Badge>
                        ) : (
                          <Text fontSize="xs" color="gray.400">
                            —
                          </Text>
                        )}
                      </Td>
                      <Td isNumeric>
                        {row.dx_review_score != null ? (
                          <Badge
                            colorScheme={
                              row.dx_review_score >= 80
                                ? 'red'
                                : row.dx_review_score >= 40
                                ? 'yellow'
                                : 'green'
                            }
                            fontSize="xs"
                          >
                            {row.dx_review_score}
                          </Badge>
                        ) : (
                          <Text fontSize="xs" color="gray.400">
                            —
                          </Text>
                        )}
                      </Td>
                      <Td>
                        <Badge colorScheme={row.is_exact_match === 1 ? 'green' : 'red'}>
                          {row.is_exact_match === 1 ? 'Yes' : 'No'}
                        </Badge>
                        <Text fontSize="10px" color="gray.500" mt={1}>
                          {row.icd_match_category}
                        </Text>
                      </Td>
                      <Td>
                        <Badge colorScheme={row.is_match_primary === 1 ? 'blue' : 'orange'}>
                          {row.is_match_primary === 1 ? 'Yes' : 'No'}
                        </Badge>
                        <Text fontSize="10px" color="gray.600" mt={1}>
                          Rev: <strong>{row.revedy_primary_icd || '-'}</strong> | Sub:{' '}
                          <strong>{row.submitted_primary_icd || '-'}</strong>
                        </Text>
                      </Td>
                      <Td fontSize="xs" fontFamily="mono" color="green.700" fontWeight="semibold">
                        {row.revedy_icds_dotted || 'None'}
                      </Td>
                      <Td fontSize="xs" fontFamily="mono" color="orange.700" fontWeight="semibold">
                        {row.submitted_icds_dotted || 'None'}
                      </Td>
                      <Td fontSize="xs" fontFamily="mono">
                        {row.icds_added_by_usn ? (
                          <Badge colorScheme="red" variant="subtle" fontSize="10px">
                            + {row.icds_added_by_usn}
                          </Badge>
                        ) : (
                          <Text fontSize="xs" color="gray.400">
                            —
                          </Text>
                        )}
                      </Td>
                      <Td fontSize="xs" fontFamily="mono">
                        {row.icds_dropped_by_usn ? (
                          <Badge colorScheme="gray" variant="subtle" fontSize="10px">
                            - {row.icds_dropped_by_usn}
                          </Badge>
                        ) : !row.icds_added_by_usn && row.is_exact_match !== 1 ? (
                          <Text fontSize="10px" color="gray.400" fontFamily="body">
                            Order diff
                          </Text>
                        ) : (
                          <Text fontSize="xs" color="gray.400">
                            —
                          </Text>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>
        </Box>
      )}

      {/* ==========================================================================
          VIEW 2: CASES FILED PRIOR TO REVEDY CODING (`filedPriorClaims`)
         ========================================================================== */}
      {activeView === 'filed_prior_to_coding' && (
        <Box>
          <Alert status="info" borderRadius="md" mb={4}>
            <AlertIcon />
            <Box fontSize="xs">
              <strong>Cases Filed Prior to Revedy Coding:</strong> These claims had complete CSV
              fields and were coded in Revedy, but the CMS-1500 <strong>Billing Date</strong> occurred{' '}
              <strong>before</strong> Revedy&apos;s first <code>USN_COMPREHENSIVE_IONM</code> coding
              run. They are excluded from the main Biller Compliance metrics and analyzed separately
              here.
            </Box>
          </Alert>

          {/* KPI Cards for Filed Prior to Revedy Coding */}
          <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4} mb={6}>
            <Box
              p={4}
              bg="white"
              borderRadius="md"
              shadow="sm"
              borderWidth="1px"
              borderLeft="4px solid #DD6B20"
            >
              <Stat>
                <StatLabel color="gray.600" fontSize="xs">
                  Total Filed Prior to Coding
                </StatLabel>
                <StatNumber fontSize="2xl" color="orange.600">
                  {postFilingStats.total.toLocaleString()}
                </StatNumber>
                <StatHelpText fontSize="xs" mb={0}>
                  Filed before Revedy ICD run
                </StatHelpText>
              </Stat>
            </Box>
            <Box p={4} bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
              <Stat>
                <StatLabel color="gray.500" fontSize="xs">
                  Case Uploaded After Filing
                </StatLabel>
                <StatNumber fontSize="2xl">
                  {postFilingStats.uploadedAfter.toLocaleString()}
                </StatNumber>
                <StatHelpText fontSize="xs" mb={0}>
                  Case created &gt; Billing Date
                </StatHelpText>
              </Stat>
            </Box>
            <Box p={4} bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
              <Stat>
                <StatLabel color="gray.500" fontSize="xs">
                  Uploaded &le; Filing, Coded After
                </StatLabel>
                <StatNumber fontSize="2xl">
                  {postFilingStats.codedAfter.toLocaleString()}
                </StatNumber>
                <StatHelpText fontSize="xs" mb={0}>
                  Avg {postFilingStats.avgDays} days after filing
                </StatHelpText>
              </Stat>
            </Box>
            <Box p={4} bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
              <Stat>
                <StatLabel color="gray.500" fontSize="xs">
                  Post-Hoc Exact Match %
                </StatLabel>
                <StatNumber fontSize="2xl" color="green.600">
                  {postFilingStats.exactPct}%
                </StatNumber>
                <StatHelpText fontSize="xs" mb={0}>
                  {postFilingStats.exactCount.toLocaleString()} matched subsequent run
                </StatHelpText>
              </Stat>
            </Box>
            <Box p={4} bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
              <Stat>
                <StatLabel color="gray.500" fontSize="xs">
                  Post-Hoc Match Primary %
                </StatLabel>
                <StatNumber fontSize="2xl" color="blue.600">
                  {postFilingStats.primaryPct}%
                </StatNumber>
                <StatHelpText fontSize="xs" mb={0}>
                  {postFilingStats.primaryCount.toLocaleString()} matched primary Dx
                </StatHelpText>
              </Stat>
            </Box>
          </SimpleGrid>

          {/* Group Breakdown Table for Cases Filed Prior to Revedy Coding */}
          <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px" mb={6}>
            <Flex justify="space-between" align="center" mb={3} flexWrap="wrap" gap={3}>
              <Heading size="sm" color="gray.700">
                Breakdown of Cases Filed Prior to Revedy Coding by Group
              </Heading>
              <Select
                size="sm"
                maxW="320px"
                value={groupCol}
                onChange={(e) => setGroupCol(e.target.value)}
              >
                {GROUP_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Flex>
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>{groupCol}</Th>
                    <Th isNumeric>Claims Filed Prior to Coding</Th>
                    <Th isNumeric>Case Uploaded After Filing</Th>
                    <Th isNumeric>Uploaded &le; Filing, Coded After</Th>
                    <Th isNumeric>Post-Hoc Exact Match %</Th>
                    <Th isNumeric>Post-Hoc Primary Match %</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {postFilingStats.groupBreakdown.map((g) => (
                    <Tr key={g.group}>
                      <Td fontWeight="semibold">{g.group}</Td>
                      <Td isNumeric fontWeight="bold">
                        {g.total.toLocaleString()}
                      </Td>
                      <Td isNumeric>{g.uploadedAfter.toLocaleString()}</Td>
                      <Td isNumeric>{g.codedAfter.toLocaleString()}</Td>
                      <Td isNumeric>{g.exactPct}%</Td>
                      <Td isNumeric>{g.primaryPct}%</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>

          {/* Table of Cases Filed Prior to Revedy Coding */}
          <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px">
            <Flex justify="space-between" align="center" flexWrap="wrap" gap={4} mb={4}>
              <Box>
                <Heading size="md" color="gray.800">
                  Cases Filed Prior to Revedy Coding ({totalMatchingPostFiling.toLocaleString()})
                </Heading>
                <Text fontSize="xs" color="gray.600">
                  Showing {filteredPostFilingRows.length.toLocaleString()} of{' '}
                  {totalMatchingPostFiling.toLocaleString()} claims filed before Revedy coding.
                </Text>
              </Box>
              <Button
                size="sm"
                colorScheme="orange"
                onClick={exportPostFilingToCSV}
                isDisabled={filteredPostFilingRows.length === 0}
              >
                Export {filteredPostFilingRows.length} Cases to CSV
              </Button>
            </Flex>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                  Timing Sub-Status Filter
                </Text>
                <Select
                  size="sm"
                  value={postFilingSubFilter}
                  onChange={(e) => setPostFilingSubFilter(e.target.value)}
                >
                  {POST_FILING_SUB_FILTER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                  Max Cases to Display
                </Text>
                <Select
                  size="sm"
                  value={postFilingLimit}
                  onChange={(e) => setPostFilingLimit(Number(e.target.value))}
                >
                  <option value={50}>50 Cases</option>
                  <option value={100}>100 Cases</option>
                  <option value={200}>200 Cases</option>
                  <option value={500}>500 Cases</option>
                  <option value={1000}>1,000 Cases</option>
                </Select>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                  Search (PID, Biller, Carrier, ICD-10)
                </Text>
                <Input
                  size="sm"
                  placeholder="Search PID, biller, carrier, ICD..."
                  value={postFilingSearch}
                  onChange={(e) => setPostFilingSearch(e.target.value)}
                />
              </Box>
            </SimpleGrid>

            <Box overflowX="auto" maxH="650px" overflowY="auto">
              <Table variant="simple" size="sm">
                <Thead position="sticky" top={0} bg="gray.100" zIndex={1}>
                  <Tr>
                    <Th>PID.Claim</Th>
                    <Th>DOS / Filed</Th>
                    <Th>Revedy Case / ICD Coded</Th>
                    <Th>Timing Reason</Th>
                    <Th>Biller (Team)</Th>
                    <Th>Region / Carrier</Th>
                    <Th isNumeric>Approval Score</Th>
                    <Th isNumeric>Needs Review</Th>
                    <Th isNumeric>Dx Review</Th>
                    <Th>Post-Hoc Match</Th>
                    <Th>Subsequent Revedy ICD-10s</Th>
                    <Th>Biller Submitted ICD-10s</Th>
                    <Th>Added by Biller</Th>
                    <Th>Dropped by Biller</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredPostFilingRows.map((row) => (
                    <Tr key={row.claim_key} _hover={{ bg: 'gray.50' }}>
                      <Td fontWeight="bold" whiteSpace="nowrap">
                        <Link
                          href={`/ionm-strict-analysis/${row.pid}`}
                          isExternal
                          color="blue.600"
                        >
                          {row.pid}.{row.claim_id} <ExternalLinkIcon mx="2px" />
                        </Link>
                      </Td>
                      <Td whiteSpace="nowrap" fontSize="xs">
                        <Text fontWeight="medium">DOS: {row.dos}</Text>
                        <Text color="orange.700" fontWeight="semibold">
                          Filed: {row.filing_date}
                        </Text>
                      </Td>
                      <Td whiteSpace="nowrap" fontSize="xs">
                        <Text color="gray.600">
                          Case Created: {row.revedy_case_created_at || 'N/A'}
                        </Text>
                        <Text color="purple.700" fontWeight="semibold">
                          ICD Coded: {row.revedy_icd_coded_at || 'N/A'}
                        </Text>
                      </Td>
                      <Td fontSize="xs">
                        <Badge
                          colorScheme={
                            row.timing_sub_status === 'Case Uploaded After Filing Date'
                              ? 'red'
                              : 'orange'
                          }
                          fontSize="10px"
                        >
                          {row.timing_sub_status}
                        </Badge>
                        {row.days_filed_before_coding != null && (
                          <Text fontSize="10px" color="gray.500" mt={1}>
                            Coded {row.days_filed_before_coding}d after filing
                          </Text>
                        )}
                      </Td>
                      <Td fontSize="xs">
                        <Text fontWeight="semibold">{row.biller_employee}</Text>
                        <Badge colorScheme="purple" fontSize="10px">
                          {row.biller_team}
                        </Badge>
                      </Td>
                      <Td fontSize="xs">
                        <Text fontWeight="medium">{row.region_org}</Text>
                        <Text color="gray.500" maxW="140px" isTruncated title={row.carrier}>
                          {row.carrier}
                        </Text>
                        {row.payer_group && (
                          <Badge colorScheme="teal" variant="subtle" fontSize="9px">
                            {row.payer_group}
                          </Badge>
                        )}
                      </Td>
                      <Td isNumeric>
                        {row.approval_score != null ? (
                          <Badge
                            colorScheme={
                              row.approval_score >= 80
                                ? 'green'
                                : row.approval_score >= 60
                                ? 'yellow'
                                : 'red'
                            }
                            fontSize="xs"
                          >
                            {row.approval_score}
                          </Badge>
                        ) : (
                          <Text fontSize="xs" color="gray.400">
                            —
                          </Text>
                        )}
                      </Td>
                      <Td isNumeric>
                        {row.needs_review_score != null ? (
                          <Badge
                            colorScheme={
                              row.needs_review_score >= 80
                                ? 'red'
                                : row.needs_review_score >= 40
                                ? 'yellow'
                                : 'green'
                            }
                            fontSize="xs"
                          >
                            {row.needs_review_score}
                          </Badge>
                        ) : (
                          <Text fontSize="xs" color="gray.400">
                            —
                          </Text>
                        )}
                      </Td>
                      <Td isNumeric>
                        {row.dx_review_score != null ? (
                          <Badge
                            colorScheme={
                              row.dx_review_score >= 80
                                ? 'red'
                                : row.dx_review_score >= 40
                                ? 'yellow'
                                : 'green'
                            }
                            fontSize="xs"
                          >
                            {row.dx_review_score}
                          </Badge>
                        ) : (
                          <Text fontSize="xs" color="gray.400">
                            —
                          </Text>
                        )}
                      </Td>
                      <Td fontSize="xs">
                        <Badge colorScheme={row.is_exact_match === 1 ? 'green' : 'gray'}>
                          Exact: {row.is_exact_match === 1 ? 'Yes' : 'No'}
                        </Badge>
                        <Text fontSize="10px" color="gray.500" mt={1}>
                          {row.icd_match_category}
                        </Text>
                      </Td>
                      <Td fontSize="xs" fontFamily="mono" color="green.700">
                        {row.revedy_icds_dotted}
                      </Td>
                      <Td fontSize="xs" fontFamily="mono" color="orange.700">
                        {row.submitted_icds_dotted}
                      </Td>
                      <Td fontSize="xs" fontFamily="mono">
                        {row.icds_added_by_usn ? (
                          <Badge colorScheme="red" variant="subtle" fontSize="10px">
                            + {row.icds_added_by_usn}
                          </Badge>
                        ) : (
                          <Text fontSize="xs" color="gray.400">
                            —
                          </Text>
                        )}
                      </Td>
                      <Td fontSize="xs" fontFamily="mono">
                        {row.icds_dropped_by_usn ? (
                          <Badge colorScheme="gray" variant="subtle" fontSize="10px">
                            - {row.icds_dropped_by_usn}
                          </Badge>
                        ) : (
                          <Text fontSize="xs" color="gray.400">
                            —
                          </Text>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>
        </Box>
      )}

      {/* ==========================================================================
          VIEW 3: EXCLUDED CLAIMS & DATA ISSUES REPORT (`excludedClaims`)
         ========================================================================== */}
      {activeView === 'excluded_issues' && (
        <Box>
          <Alert status="warning" borderRadius="md" mb={4}>
            <AlertIcon />
            <Box fontSize="xs">
              <strong>Strict Zero-Fallback Validation:</strong> Claims below are excluded from
              Biller Compliance metrics because one or more required CSV fields were empty/unmapped
              (e.g. blank <code>Biller Employee</code>, blank <code>Insurance Company Name</code>,
              blank <code>Diagnosis Code 1</code>) or the case was not found / not coded in Revedy.
            </Box>
          </Alert>

          {/* Summary KPIs for Excluded Claims */}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
            <Box
              p={4}
              bg="white"
              borderRadius="md"
              shadow="sm"
              borderWidth="1px"
              borderLeft="4px solid #DC2626"
            >
              <Stat>
                <StatLabel color="gray.600" fontSize="xs">
                  Total Excluded Claims
                </StatLabel>
                <StatNumber fontSize="2xl" color="red.600">
                  {excludedStats.totalExcluded.toLocaleString()}
                </StatNumber>
                <StatHelpText fontSize="xs" mb={0}>
                  Excluded from compliance analysis
                </StatHelpText>
              </Stat>
            </Box>
            <Box p={4} bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
              <Stat>
                <StatLabel color="gray.500" fontSize="xs">
                  Claims w/ Missing Required CSV Fields
                </StatLabel>
                <StatNumber fontSize="2xl" color="orange.600">
                  {excludedStats.csvIssuesCount.toLocaleString()}
                </StatNumber>
                <StatHelpText fontSize="xs" mb={0}>
                  Blank Biller Employee, Carrier, ICD-10, etc.
                </StatHelpText>
              </Stat>
            </Box>
            <Box p={4} bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
              <Stat>
                <StatLabel color="gray.500" fontSize="xs">
                  Claims Missing Revedy Case / Coding Data
                </StatLabel>
                <StatNumber fontSize="2xl" color="purple.600">
                  {excludedStats.caseIssuesCount.toLocaleString()}
                </StatNumber>
                <StatHelpText fontSize="xs" mb={0}>
                  Case not in Revedy DB or no USN_COMPREHENSIVE_IONM run
                </StatHelpText>
              </Stat>
            </Box>
          </SimpleGrid>

          {/* Issue Breakdown Table */}
          <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px" mb={6}>
            <Heading size="sm" color="gray.700" mb={3}>
              Data Issues Breakdown (Click any issue row to filter claims below)
            </Heading>
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Issue / Exclusion Reason</Th>
                    <Th isNumeric>Affected Claims</Th>
                    <Th isNumeric>% of Total Uploaded Claims</Th>
                    <Th>Filter Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {baseIssueSummary.map((iss) => (
                    <Tr
                      key={iss.issue_reason}
                      _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                      bg={selectedIssueReason === iss.issue_reason ? 'red.50' : undefined}
                      onClick={() =>
                        setSelectedIssueReason(
                          selectedIssueReason === iss.issue_reason ? 'ALL' : iss.issue_reason
                        )
                      }
                    >
                      <Td fontWeight="semibold">{iss.issue_reason}</Td>
                      <Td isNumeric fontWeight="bold">
                        {iss.claim_count.toLocaleString()}
                      </Td>
                      <Td isNumeric>{iss.pct_of_total_claims}%</Td>
                      <Td>
                        <Button size="xs" variant="outline" colorScheme="red">
                          {selectedIssueReason === iss.issue_reason ? 'Clear Filter' : 'Filter'}
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>

          {/* Excluded Claims Table */}
          <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px">
            <Flex justify="space-between" align="center" flexWrap="wrap" gap={4} mb={4}>
              <Box>
                <Heading size="md" color="gray.800">
                  Excluded Claims Detail ({totalMatchingExcluded.toLocaleString()})
                </Heading>
                <Text fontSize="xs" color="gray.600">
                  Showing {filteredExcludedRows.length.toLocaleString()} of{' '}
                  {totalMatchingExcluded.toLocaleString()} excluded claims.
                </Text>
              </Box>
              <Button
                size="sm"
                colorScheme="red"
                onClick={exportExcludedToCSV}
                isDisabled={filteredExcludedRows.length === 0}
              >
                Export {filteredExcludedRows.length} Excluded Claims to CSV
              </Button>
            </Flex>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                  Filter by Issue Reason
                </Text>
                <Select
                  size="sm"
                  value={selectedIssueReason}
                  onChange={(e) => setSelectedIssueReason(e.target.value)}
                >
                  <option value="ALL">
                    All Active Excluded Claims ({baseExcludedClaims.length.toLocaleString()})
                  </option>
                  {baseIssueSummary.map((iss) => (
                    <option key={iss.issue_reason} value={iss.issue_reason}>
                      {iss.issue_reason} ({iss.claim_count.toLocaleString()})
                    </option>
                  ))}
                </Select>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                  Max Excluded Claims to Display
                </Text>
                <Select
                  size="sm"
                  value={excludedLimit}
                  onChange={(e) => setExcludedLimit(Number(e.target.value))}
                >
                  <option value={50}>50 Claims</option>
                  <option value={100}>100 Claims</option>
                  <option value={200}>200 Claims</option>
                  <option value={500}>500 Claims</option>
                  <option value={1000}>1,000 Claims</option>
                </Select>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                  Search (PID, Biller, Carrier, Issue)
                </Text>
                <Input
                  size="sm"
                  placeholder="Search PID, issue, biller, carrier..."
                  value={excludedSearch}
                  onChange={(e) => setExcludedSearch(e.target.value)}
                />
              </Box>
            </SimpleGrid>

            <Box overflowX="auto" maxH="650px" overflowY="auto">
              <Table variant="simple" size="sm">
                <Thead position="sticky" top={0} bg="gray.100" zIndex={1}>
                  <Tr>
                    <Th>PID.Claim</Th>
                    <Th>DOS / Filed</Th>
                    <Th>Exclusion / Issue Reason(s)</Th>
                    <Th>Biller Employee (Team)</Th>
                    <Th>Region / Carrier / Ins Type</Th>
                    <Th>Submitted ICD-10s</Th>
                    <Th>Revedy Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredExcludedRows.map((row) => (
                    <Tr key={row.claim_key} _hover={{ bg: 'gray.50' }}>
                      <Td fontWeight="bold" whiteSpace="nowrap">
                        <Link
                          href={`/ionm-strict-analysis/${row.pid}`}
                          isExternal
                          color="blue.600"
                        >
                          {row.pid}.{row.claim_id} <ExternalLinkIcon mx="2px" />
                        </Link>
                      </Td>
                      <Td whiteSpace="nowrap" fontSize="xs">
                        <Text fontWeight="medium">DOS: {row.dos || 'MISSING'}</Text>
                        <Text color="gray.500">Filed: {row.filing_date || 'MISSING'}</Text>
                      </Td>
                      <Td fontSize="xs">
                        <VStack align="start" spacing={1}>
                          {(row.issue_reasons || [row.primary_issue]).map((iss) => (
                            <Badge key={iss} colorScheme="red" fontSize="10px">
                              {iss}
                            </Badge>
                          ))}
                        </VStack>
                      </Td>
                      <Td fontSize="xs">
                        <Text fontWeight="semibold">
                          {row.biller_employee || <Badge colorScheme="red">BLANK</Badge>}
                        </Text>
                        {row.biller_team && (
                          <Badge colorScheme="purple" fontSize="10px">
                            {row.biller_team}
                          </Badge>
                        )}
                      </Td>
                      <Td fontSize="xs">
                        <Text fontWeight="medium">{row.region_org || 'BLANK'}</Text>
                        <Text color="gray.600">
                          Carrier: {row.carrier || <Badge colorScheme="red">BLANK</Badge>}
                        </Text>
                        <Text color="gray.500" fontSize="10px">
                          Ins Type: {row.insurance_type || 'BLANK'}
                        </Text>
                      </Td>
                      <Td fontSize="xs" fontFamily="mono">
                        {row.submitted_icds_dotted || <Badge colorScheme="red">NONE</Badge>}
                      </Td>
                      <Td fontSize="xs">
                        <Badge colorScheme={row.in_revedy_db === 1 ? 'green' : 'red'} mr={1}>
                          DB: {row.in_revedy_db === 1 ? 'Yes' : 'No'}
                        </Badge>
                        <Badge colorScheme={row.has_revedy_icd === 1 ? 'blue' : 'orange'}>
                          Coded: {row.has_revedy_icd === 1 ? 'Yes' : 'No'}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default USNBillerComplianceTab;
